import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_joywin-photo-deals/artifacts/7835b5me_new%20logoo.PNG";
const JMAJOR_LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_c5db327b-4bf4-420f-890a-b7ce4db2a362/artifacts/jdkdmeym_Jmajor-Photoroom.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();

  const isDJPage = location.pathname.startsWith('/dj') || location.pathname === '/music';
  const currentLogo = isDJPage ? JMAJOR_LOGO : LOGO_URL;
  const logoAlt = isDJPage ? 'J MAJOR' : "Joywin's Clicks";

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Packages', path: '/packages' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'DJ', path: '/dj', style: { color: '#EF0000' } },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 ${isDJPage ? '' : 'glass'}`}
      style={isDJPage ? { background: 'linear-gradient(135deg, #0a0000, #1a0508, #0d0d0d)', borderBottom: '1px solid rgba(255,255,255,0.05)' } : {}}
      data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to={isDJPage ? "/dj" : "/"} className="flex items-center" data-testid="nav-logo">
            <img 
              src={currentLogo} 
              alt={logoAlt} 
              className={isDJPage ? "h-8 md:h-10 object-contain" : "h-14 md:h-16 object-contain"}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm tracking-wide transition-colors duration-300 ${
                  isActive(link.path)
                    ? link.style ? '' : 'text-[#D4AF37]'
                    : link.style ? '' : 'text-[#A1A1AA] hover:text-[#EDEDED]'
                }`}
                style={link.style ? { color: link.style.color, fontWeight: 600 } : {}}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </Link>
            ))}

            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                <Link
                  to="/dashboard"
                  className={`text-sm tracking-wide transition-colors duration-300 ${
                    isActive('/dashboard')
                      ? 'text-[#D4AF37]'
                      : 'text-[#A1A1AA] hover:text-[#EDEDED]'
                  }`}
                  data-testid="nav-link-dashboard"
                >
                  Dashboard
                </Link>
                <div className="flex items-center gap-2 text-[#A1A1AA]">
                  <User className="w-4 h-4" />
                  <span className="text-sm">{user?.name}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-[#A1A1AA] hover:text-[#EDEDED] transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <Link to={isDJPage ? "/login?type=dj" : "/login"} data-testid="nav-login-btn">
                <Button
                  variant="outline"
                  className={isDJPage
                    ? 'border-[#EF0000] text-[#EF0000] hover:bg-[#EF0000] hover:text-white'
                    : 'border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black'}
                >
                  Login
                </Button>
              </Link>
            )}

            <Link to={isDJPage ? "/dj/book" : "/book"} data-testid="nav-book-btn">
              <Button className={isDJPage
                ? 'rounded-full text-white bg-[#EF0000] hover:bg-[#991B1B]'
                : 'gold-gradient-btn rounded-full'}
              >
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#EDEDED]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-2 text-lg ${
                    isActive(link.path) && !link.style ? 'text-[#D4AF37]' : !link.style ? 'text-[#A1A1AA]' : ''
                  }`}
                  style={link.style ? { color: link.style.color, fontWeight: 600 } : {}}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    className="block py-2 text-lg text-[#A1A1AA]"
                    onClick={() => setIsOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="block py-2 text-lg text-[#A1A1AA]"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="block py-2 text-lg text-[#A1A1AA]"
                  onClick={() => setIsOpen(false)}
                >
                  Login
                </Link>
              )}
              
              <Link
                to="/book"
                className="block"
                onClick={() => setIsOpen(false)}
              >
                <Button className="w-full gold-gradient-btn rounded-full mt-4">
                  Book Now
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
